/* eslint-disable */
import React, { useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Loading from '../../components/misc/Loading';
import useDocumentTitle from '../../components/useDocumentTitle';

const CLICK_ENDPOINT = 'https://nmt.loveworldapis.com/api/affiliate/links/click';
const X_API_KEY = 'ee115610-738b-4e7b-97e2-446468ba550c';

/**
 * Destination codes → redirect targets.
 * The token `{code}` is replaced with the affiliate code at runtime.
 */
const DEST_MAP: Record<string, string> = {
  x6w: '/',
  '9rs': '/subscription?affiliate={code}&promo=affiliate_discount',
};

const DEFAULT_DEST = '/';

interface ClickMeta {
  page:     string;
  referrer: string | null;
  dest?:    string;
  utm?:     Record<string, string>;
}

interface ClickBody {
  code: string;
  ref:  string | null;
  meta: ClickMeta;
}

function resolveDestination(dest: string | undefined, code: string): string {
  if (!dest) return DEFAULT_DEST;
  const template = DEST_MAP[dest.toLowerCase()];
  if (!template) return DEFAULT_DEST;
  return template.replace('{code}', encodeURIComponent(code));
}

/**
 * Route: /refferal/:dest/:ref/:code
 *
 * Waits for the tracking API to fully respond before redirecting.
 * On network failure the redirect still happens so the user is never stuck.
 */
export default function ReferralRedirect(): JSX.Element {
  useDocumentTitle('Loading… | Pastor Chris Digital Library');

  const { dest, ref, code } = useParams<{
    dest:  string;
    ref?:  string;
    code?: string;
  }>();

  const fireAndRedirect = useCallback(async (): Promise<void> => {
    const affiliateCode  = code ?? '';
    const redirectTarget = resolveDestination(dest, affiliateCode);

    // ── 1. Persist affiliate code in localStorage ──
    try {
      const raw = localStorage.getItem('pcdl.store');
      let parsed: unknown;
      try   { parsed = raw ? JSON.parse(raw) : null; }
      catch { parsed = raw ?? null; }

      const userObj: Record<string, unknown> =
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? { ...(parsed as Record<string, unknown>), affiliate: affiliateCode }
          : { value: parsed ?? null, affiliate: affiliateCode };

      localStorage.setItem('pcdl.store', JSON.stringify(userObj));
    } catch {
      // non-fatal — storage may be blocked
    }

    // ── 2. Build meta ──
    const params = new URLSearchParams(window.location.search);

    const utmKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
    ] as const;

    const utm: Record<string, string> = {};
    utmKeys.forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v;
    });

    const meta: ClickMeta = {
      page:     params.get('page') ?? window.location.pathname,
      referrer: document.referrer || null,
      dest:     dest ?? undefined,
      ...(Object.keys(utm).length ? { utm } : {}),
    };

    // ── 3. Fire click tracking — fully await the response ──
    const body: ClickBody = {
      code: affiliateCode,
      ref:  ref ?? null,
      meta,
    };

    try {
      await fetch(CLICK_ENDPOINT, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': X_API_KEY },
        body:    JSON.stringify(body),
      });
    } catch {
      // Network failure — still redirect so the user is never stuck
    }

    // ── 4. Redirect only after API is done ──
    window.location.replace(redirectTarget);
  }, [dest, ref, code]);

  useEffect(() => {
    fireAndRedirect();
  }, [fireAndRedirect]);

  return <Loading />;
}